import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    archiveRentalAssetType,
    createRentalAssetType,
    getRentalAssetType,
    listRentalAssetTypes,
    unarchiveRentalAssetType,
    updateRentalAssetType,
} from './api'
import {rentalAssetKeys} from './query-keys'
import type {CreateRentalAssetTypeInput, UpdateRentalAssetTypeInput} from './schemas'

type UseRentalAssetTypesOptions = {
    enabled?: boolean
    includeArchived?: boolean
}

export function useRentalAssetTypes(options?: UseRentalAssetTypesOptions) {
    const includeArchived = options?.includeArchived
    return useQuery({
        queryKey: rentalAssetKeys.types(includeArchived),
        queryFn: () => listRentalAssetTypes({includeArchived}),
        enabled: options?.enabled ?? true,
    })
}

export function useRentalAssetType(id: string) {
    return useQuery({
        queryKey: rentalAssetKeys.type(id),
        queryFn: () => getRentalAssetType(id),
    })
}

export function useCreateRentalAssetType() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateRentalAssetTypeInput) => createRentalAssetType(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: ['rental-asset-types']})
        },
    })
}

export function useUpdateRentalAssetType() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, input}: {id: string; input: UpdateRentalAssetTypeInput}) =>
            updateRentalAssetType(id, input),
        onSuccess: (_data, {id}) => {
            void queryClient.invalidateQueries({queryKey: ['rental-asset-types']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.type(id)})
        },
    })
}

export function useArchiveRentalAssetType() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => archiveRentalAssetType(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: ['rental-asset-types']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.type(id)})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.stockSummaries()})
        },
    })
}

export function useUnarchiveRentalAssetType() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => unarchiveRentalAssetType(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: ['rental-asset-types']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.type(id)})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.stockSummaries()})
        },
    })
}
